import type { Metadata } from '@/services/property/property.type'

const normalizeKey = (key: string | undefined): string => {
  if (typeof key !== 'string') return ''
  return key.trim().toLowerCase()
}

const sanitizeKey = (key: string | undefined): string => {
  if (typeof key !== 'string') return ''
  return key.trim()
}

const isSameKey = (
  entryKey: string | undefined,
  inputKey: string | undefined,
): boolean => {
  const a = normalizeKey(entryKey)
  const b = normalizeKey(inputKey)
  if (!a || !b) return false
  return a === b
}

const normalizeValue = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  return trimmed.length ? trimmed : ''
}

const mergeMetadataValue = (
  target: string | undefined,
  source: string | undefined,
): string | undefined => {
  const a = normalizeValue(target)
  const b = normalizeValue(source)

  if (a == null) return b
  if (b == null) return a

  // Prefer the non-empty value when the other is empty string.
  if (a === '') return b
  if (b === '') return a

  if (a === b) return a

  // Deterministic merge: preserve order, avoid duplicates.
  const parts = [a, b]
  const unique: string[] = []
  for (const part of parts) {
    if (!unique.includes(part)) unique.push(part)
  }
  return unique.join('\n')
}

export const getMetadataValue = (
  metadata: Metadata[] | undefined,
  key: string,
): string | undefined => {
  if (!Array.isArray(metadata)) return undefined
  const normalizedKey = normalizeKey(key)
  if (!normalizedKey) return undefined
  const item = metadata.find((entry) => isSameKey(entry.key, normalizedKey))
  return item?.value
}

export const upsertMetadataValue = (
  metadata: Metadata[] | undefined,
  key: string,
  value: string | undefined,
): Metadata[] => {
  const sanitizedKey = sanitizeKey(key)
  if (!sanitizedKey) return Array.isArray(metadata) ? [...metadata] : []
  const nextValue = normalizeValue(value)
  const next = Array.isArray(metadata) ? [...metadata] : []
  const existingIndex = next.findIndex((entry) => isSameKey(entry.key, sanitizedKey))

  if (existingIndex >= 0) {
    next[existingIndex] = {
      ...next[existingIndex],
      key: sanitizedKey,
      value: nextValue,
    }
    return next
  }

  next.push({ key: sanitizedKey, value: nextValue })
  return next
}

export const renameMetadataKey = (
  metadata: Metadata[] | undefined,
  fromKey: string,
  toKey: string,
): Metadata[] => {
  const sanitizedFromKey = sanitizeKey(fromKey)
  const sanitizedToKey = sanitizeKey(toKey)
  if (!sanitizedFromKey || !sanitizedToKey) {
    return Array.isArray(metadata) ? [...metadata] : []
  }
  if (sanitizedFromKey === sanitizedToKey) {
    return Array.isArray(metadata) ? [...metadata] : []
  }

  if (!Array.isArray(metadata)) return []

  const hasFromKey = metadata.some((entry) =>
    isSameKey(entry.key, sanitizedFromKey),
  )
  if (!hasFromKey) return [...metadata]

  const hasToKey = metadata.some((entry) => isSameKey(entry.key, sanitizedToKey))

  let merged: Metadata | undefined
  const absorb = (entry: Metadata) => {
    const matchesFrom = isSameKey(entry.key, sanitizedFromKey)
    const next: Metadata =
      matchesFrom ? { ...entry, key: sanitizedToKey } : { ...entry }

    if (!merged) {
      merged = next
      return
    }

    const mergedType = merged.type ?? next.type
    merged = {
      ...merged,
      key: sanitizedToKey,
      ...(typeof mergedType === 'string' ? { type: mergedType } : {}),
      value: mergeMetadataValue(merged.value, next.value),
    }
  }

  // When `toKey` already exists, prioritize it and then merge `fromKey` into it.
  if (hasToKey) {
    for (const entry of metadata) {
      if (isSameKey(entry.key, sanitizedToKey)) absorb(entry)
    }
    for (const entry of metadata) {
      if (isSameKey(entry.key, sanitizedFromKey)) absorb(entry)
    }
  } else {
    for (const entry of metadata) {
      if (isSameKey(entry.key, sanitizedFromKey)) absorb(entry)
    }
  }

  if (!merged) return [...metadata]

  const next: Metadata[] = []
  let inserted = false

  for (const entry of metadata) {
    if (
      isSameKey(entry.key, sanitizedFromKey) ||
      isSameKey(entry.key, sanitizedToKey)
    ) {
      if (!inserted) {
        next.push(merged)
        inserted = true
      }
      continue
    }
    next.push(entry)
  }

  return next
}

export const deleteMetadataKey = (
  metadata: Metadata[] | undefined,
  key: string,
): Metadata[] => {
  const sanitizedKey = sanitizeKey(key)
  if (!Array.isArray(metadata) || !sanitizedKey)
    return Array.isArray(metadata) ? [...metadata] : []
  return metadata.filter((entry) => !isSameKey(entry.key, sanitizedKey))
}
